import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import useAuth from '../../../Hooks/useAuth';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import ConfigurationListPaginationFooter from '../../../components/AdminDashboard/Configuration/ConfigurationListPaginationFooter';
import { useInlineRowActions, ROW_ACTIONS_MENU_HEIGHT } from '../../../Hooks/useInlineRowActions';
import { useConfigurationListPagination } from '../../../Hooks/useConfigurationListPagination';
import { urlNameSegment } from '../../../utils/slugify';
import {
  AdminPageTitle,
  AdminPanelTitle,
  AdminTableHeader,
  AdminTableBody,
  AdminTableMeta,
  AdminStatusBadge,
  AdminSupportingText,
  AdminHeaderAction,
  AdminRowMenuItem,
  AdminRowActionsButton,
  AdminFixedRowActionsMenu,
  AdminRowMenuIcon,
  ADMIN_PAGE_TITLE_CLASS,
  ADMIN_TABLE_CLASS,
} from '../shared/adminUx';
import { isTeamOwnerActor } from '../../../data/teamConfig';
import {
  KEYWORD_CONFIG_ROUTE,
  MESSAGES,
  buildSampleKeywordConfigRules,
  formatConfigLastUpdatedDisplay,
  getKeywordConfigSources,
  getListActionsForKeywordRule,
} from '../../../data/keywordsConfig';

const tableCols =
  'minmax(168px, 1.35fr) minmax(76px, 0.55fr) minmax(120px, 0.9fr) minmax(128px, 0.95fr) minmax(160px, 1.35fr) minmax(80px, 0.55fr) minmax(100px, 0.65fr) minmax(140px, 1fr) minmax(56px, 0.22fr)';

const TABLE_MIN_WIDTH = '1320px';

const Keywords = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { auth } = useAuth();
  const canAccess = isTeamOwnerActor(auth);
  const sources = useMemo(() => getKeywordConfigSources(), []);

  const [rules, setRules] = useState(() => buildSampleKeywordConfigRules(sources));
  const [feedback, setFeedback] = useState('');
  const [feedbackError, setFeedbackError] = useState(false);

  const {
    rowActionsOpen,
    rowActionsRefs,
    tableScrollRef,
    rowActionsMenuLayout,
    rowActionsMenuRef,
    rowActionsMenuPlacementReady,
    toggleRowActions,
    closeRowActions,
  } = useInlineRowActions(ROW_ACTIONS_MENU_HEIGHT.DOUBLE);

  const {
    currentPage,
    setCurrentPage,
    totalPages,
    paginatedItems: paginatedRules,
    rangeStart,
    rangeEnd,
    totalCount,
    showPagination,
  } = useConfigurationListPagination(rules);

  useEffect(() => {
    const incoming = location.state?.rules;
    const message = location.state?.feedback;
    if (Array.isArray(incoming) || message) {
      if (Array.isArray(incoming)) setRules(incoming);
      if (message) {
        setFeedback(message);
        setFeedbackError(false);
      }
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  const openRuleView = (rule) => {
    navigate(`${KEYWORD_CONFIG_ROUTE}/${urlNameSegment(rule.ruleName)}`, {
      state: { rule, existingRules: rules },
    });
  };

  const openRuleEdit = (rule) => {
    navigate(`${KEYWORD_CONFIG_ROUTE}/${urlNameSegment(rule.ruleName)}/edit`, {
      state: { rule, existingRules: rules },
    });
  };

  const openRuleAdd = () => {
    navigate(`${KEYWORD_CONFIG_ROUTE}/add`, {
      state: { existingRules: rules },
    });
  };

  if (!canAccess) {
    return (
      <AdminLayout>
        <AdminPageTitle>Keyword Rules</AdminPageTitle>
        <AdminSupportingText className="text-mediumgray mt-4">
          Configuration is developer-controlled. You do not have permission to view Keyword Rules.
        </AdminSupportingText>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="min-w-0">
        <div
          className={`flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 ${ADMIN_PAGE_TITLE_CLASS}`}
        >
          <AdminPageTitle className="!mb-0">Keyword Rules</AdminPageTitle>
          <AdminHeaderAction onClick={openRuleAdd}>Add Rule</AdminHeaderAction>
        </div>

        {feedback ? (
          <AdminSupportingText
            className={`mb-4 ${feedbackError ? 'text-alertred' : 'text-freshgreen'}`}
          >
            {feedback}
          </AdminSupportingText>
        ) : null}

        <DashboardMainContainer className={`${ADMIN_TABLE_CLASS} max-w-full min-w-0`}>
          <div ref={tableScrollRef} className="w-full min-w-0 overflow-x-auto">
            {rules.length === 0 ? (
              <div className="py-10 px-4 text-center space-y-4">
                <AdminPanelTitle className="text-offblack">{MESSAGES.empty}</AdminPanelTitle>
                <div className="flex justify-center pt-2">
                  <AdminHeaderAction onClick={openRuleAdd}>Add Rule</AdminHeaderAction>
                </div>
              </div>
            ) : (
              <>
                <div
                  className="grid w-full py-3 text-offblack border-b border-lightgray px-4 gap-x-3 gap-y-2"
                  style={{ gridTemplateColumns: tableCols, minWidth: TABLE_MIN_WIDTH }}
                >
                  <div className="flex items-center min-w-0">
                    <AdminTableHeader className="truncate">Rule Name</AdminTableHeader>
                  </div>
                  <div className="flex items-center min-w-0">
                    <AdminTableHeader className="truncate">Rule ID</AdminTableHeader>
                  </div>
                  <div className="flex items-center min-w-0">
                    <AdminTableHeader className="truncate">Application Area</AdminTableHeader>
                  </div>
                  <div className="flex items-center min-w-0">
                    <AdminTableHeader className="truncate">Rule Scope</AdminTableHeader>
                  </div>
                  <div className="flex items-center min-w-0">
                    <AdminTableHeader className="truncate">Scope Selection</AdminTableHeader>
                  </div>
                  <div className="flex items-center min-w-0">
                    <AdminTableHeader className="truncate">Keyword Use</AdminTableHeader>
                  </div>
                  <div className="flex items-center min-w-0 shrink-0">
                    <AdminTableHeader>Configuration Status</AdminTableHeader>
                  </div>
                  <div className="flex items-center min-w-0">
                    <AdminTableHeader className="truncate">Last Updated</AdminTableHeader>
                  </div>
                  <div className="flex items-center shrink-0 justify-center">
                    <AdminTableHeader>Actions</AdminTableHeader>
                  </div>
                </div>

                {paginatedRules.map((rule) => {
                  const actions = getListActionsForKeywordRule(rule);
                  return (
                    <div
                      key={rule.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => openRuleView(rule)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          openRuleView(rule);
                        }
                      }}
                      className="grid w-full py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-x-3 gap-y-2 cursor-pointer rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-primaryblue focus-visible:ring-offset-1"
                      style={{ gridTemplateColumns: tableCols, minWidth: TABLE_MIN_WIDTH }}
                    >
                      <div className="flex items-center min-w-0 overflow-hidden">
                        <AdminTableBody className="truncate block" title={rule.ruleName}>
                          {rule.ruleName}
                        </AdminTableBody>
                      </div>
                      <div className="flex items-center min-w-0 overflow-hidden">
                        <AdminTableMeta className="truncate block" title={rule.id}>
                          {rule.id}
                        </AdminTableMeta>
                      </div>
                      <div className="flex items-center min-w-0 overflow-hidden">
                        <AdminTableBody className="truncate block">{rule.applicationArea}</AdminTableBody>
                      </div>
                      <div className="flex items-center min-w-0 overflow-hidden">
                        <AdminTableBody className="truncate block" title={rule.ruleScope}>
                          {rule.ruleScope}
                        </AdminTableBody>
                      </div>
                      <div className="flex items-center min-w-0 overflow-hidden">
                        <AdminTableBody
                          className="truncate block"
                          title={rule.scopeSelectionDisplay}
                        >
                          {rule.scopeSelectionDisplay}
                        </AdminTableBody>
                      </div>
                      <div className="flex items-center min-w-0 overflow-hidden">
                        <AdminTableBody className="truncate block">{rule.keywordUse}</AdminTableBody>
                      </div>
                      <div className="flex items-center min-w-0 shrink-0">
                        <AdminStatusBadge
                          status={rule.configurationStatus}
                          label={rule.configurationStatus}
                        />
                      </div>
                      <div className="flex items-center min-w-0 overflow-hidden">
                        <AdminTableBody
                          className="truncate block"
                          title={formatConfigLastUpdatedDisplay(
                            rule.lastUpdatedOn,
                            rule.lastUpdatedBy,
                          )}
                        >
                          {formatConfigLastUpdatedDisplay(rule.lastUpdatedOn, rule.lastUpdatedBy)}
                        </AdminTableBody>
                      </div>
                      <div
                        className="flex items-center justify-center shrink-0 overflow-visible"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div
                          className="relative inline-flex"
                          ref={(el) => {
                            rowActionsRefs.current[rule.id] = el;
                          }}
                        >
                          <AdminRowActionsButton
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleRowActions(rule.id);
                            }}
                            aria-expanded={!!rowActionsOpen[rule.id]}
                            title="Actions"
                            aria-label="Row actions"
                          >
                            <AdminRowMenuIcon />
                          </AdminRowActionsButton>
                          {rowActionsOpen[rule.id] && rowActionsMenuLayout ? (
                            <AdminFixedRowActionsMenu
                              layout={rowActionsMenuLayout}
                              menuRef={rowActionsMenuRef}
                              placementReady={rowActionsMenuPlacementReady}
                            >
                              {actions.includes('view') ? (
                                <AdminRowMenuItem
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    closeRowActions();
                                    openRuleView(rule);
                                  }}
                                >
                                  View
                                </AdminRowMenuItem>
                              ) : null}
                              {actions.includes('edit') ? (
                                <AdminRowMenuItem
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    closeRowActions();
                                    openRuleEdit(rule);
                                  }}
                                >
                                  Edit
                                </AdminRowMenuItem>
                              ) : null}
                            </AdminFixedRowActionsMenu>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </DashboardMainContainer>

        <ConfigurationListPaginationFooter
          show={showPagination && rules.length > 0}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          totalCount={totalCount}
          currentPage={currentPage}
          totalPages={totalPages}
          onPrev={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
          onNext={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
          goToPage={setCurrentPage}
        />
      </div>
    </AdminLayout>
  );
};

export default Keywords;
